import { ArrowRightIcon, ClockIcon } from '@heroicons/react/20/solid';

import { usePendingPicksCount } from '@/renderer/utils/hooks/usePendingPicksCount';

/**
 * Persistent pinned affordance at the top of the Release Center pointing the
 * member to pending picks they're entitled to. It is NOT an empty-state: a
 * returning subscriber can have a non-empty feed (old receipts, free messages,
 * announcements) yet still have new entitled pending picks with no receipt —
 * receipt-gated surfaces (this feed) never show those, so without this pointer
 * they're lost. It shows whenever the member's ghost-aware pending count > 0
 * (the same universal signal as the nav badge), regardless of feed state, and
 * self-clears when the count hits 0 (picks viewed/received or graded).
 *
 * The picks are viewable on My Results, whose pending section already renders the
 * ghost picks in full detail.
 */
export function PendingPicksNotice() {
  const { count } = usePendingPicksCount();

  if (!count) {
    return null;
  }

  return (
    <a
      href="/results"
      className="flex flex-row items-center justify-between gap-2 bg-emerald-50 p-4 transition hover:bg-emerald-100 dark:bg-emerald-500/10 dark:hover:bg-emerald-500/15"
    >
      <div className="flex flex-row items-center gap-2 text-emerald-800 text-sm dark:text-emerald-300">
        <ClockIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
        <span>
          <strong>
            You have {count} pending {count === 1 ? 'pick' : 'picks'}.
          </strong>{' '}
          They&apos;re ready to view on your results.
        </span>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 font-semibold text-emerald-700 text-sm dark:text-emerald-300">
        View
        <ArrowRightIcon height={16} className="size-4" aria-hidden="true" />
      </span>
    </a>
  );
}
